<script setup>
import { computed, nextTick, onMounted, ref, watch, toRef } from "vue";
import { useStore } from "vuex";
import { track } from "jiffy-analytics";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import UseSvg from "common/vue/components/UseSvg.vue";
import { BUSINESS_LINE } from "common/utils/app-registry/contants";
import { readCookie } from "common/utils/cookie";
import MadeDeliveryEstimates from "common/vue/components/MadeDeliveryEstimates.vue";
import SizeQuantityControl from "./SizeQuantityControl.vue";
import ProductDetailsV2 from "./ProductDetailsV2.vue";
import ProductDetails from "./ProductDetails.vue";
import ColorPicker from "./ColorPicker.vue";

const emit = defineEmits(["close", "update:totalPrice"]);

const props = defineProps({
  lineItem: {
    type: Object,
    required: true
  },
  sizes: {
    type: Array,
    default: () => []
  },
  canvasImage: {
    type: String,
    default: null
  },
  isCart: {
    type: Boolean,
    default: true
  },
  addMode: {
    type: Boolean,
    default: false
  },
  addAnotherProduct: {
    type: Boolean,
    default: false
  },
  isReorder: {
    type: Boolean,
    default: false
  }
});

const store = useStore();
const lineItem = toRef(props, "lineItem");

const errorMessage = ref(null);
const isDisabled = ref(false);
const isLoading = ref(true);
const isArtworksLoading = ref(true);
const modal = ref(null);
const model = ref({});
const timeStamp = ref(null);
const selectedColor = ref(null);
const exitReason = ref(null);
const composite = ref(lineItem.value.compositePreview);
const isSameColor = ref(selectedColor.value?.colorName === lineItem.value.productColor);

const variantsData = computed(() =>
  store.getters.variantsData.map(variant => ({
    ...variant,
    quantity: props.addMode
      ? 0
      : props.sizes.find(({ size }) => size === variant.size_label)?.quantity || 0
  }))
);

const productData = computed(() => store.getters.productData);

const compositions = computed(() => productData.value?.compositions || []);

// Calculate the current assembly group's base price (all variants at tier 0)
const currentAssemblyGroupBasePrice = computed(() =>
  props.sizes.reduce((acc, sizeItem) => {
    const variant = variantsData.value.find(v => v.size_label === sizeItem.size);
    if (variant?.price_tiers) {
      const basePrice = parseFloat(variant.price_tiers["0"]) || 0;
      return acc + basePrice * sizeItem.quantity;
    }
    return acc;
  }, 0)
);

// Calculate total using base prices (tier 0) to determine proper bulk discount tier
const totalBasePrice = computed(() =>
  Object.values(model.value || {})
    .filter(item => !item.isExcluded)
    .reduce((acc, item) => {
      const basePrice = item.basePrice || 0;
      return acc + basePrice * item.quantity;
    }, 0)
);

// Calculate the cart total excluding current assembly group
const otherItemsTotal = computed(
  () => store.getters.productsSubtotal - currentAssemblyGroupBasePrice.value
);

// Determine the proper tier based on other items + current base prices
const effectiveTier = computed(() => {
  const comprehensiveTotal = otherItemsTotal.value + totalBasePrice.value;

  // Get tier thresholds from the first variant (they should all be the same)
  const firstVariant = variantsData.value[0];
  if (!firstVariant?.price_tiers) return 0;

  const tiers = Object.keys(firstVariant.price_tiers)
    .map(t => parseFloat(t))
    .sort((a, b) => b - a); // Sort descending

  return tiers.find(tier => comprehensiveTotal >= tier) || 0;
});

// Calculate final display total using current effective tier
const totalPrice = computed(() =>
  Object.values(model.value || {})
    .filter(item => !item.isExcluded)
    .reduce((acc, item) => {
      const variant = variantsData.value.find(v => v.id === item.id);
      const tierPrice = variant?.price_tiers?.[effectiveTier.value]
        ? parseFloat(variant.price_tiers[effectiveTier.value])
        : parseFloat(variant?.price_tiers["0"] || 0);
      return acc + tierPrice * item.quantity;
    }, 0)
);

const totalQuantity = computed(() =>
  Object.values(model.value || {})
    .filter(item => !item.isExcluded)
    .reduce((acc, item) => acc + item.quantity, 0)
);

const disableAddToCart = computed(() => isDisabled.value || totalQuantity.value === 0);
const isSample = computed(() => Boolean(lineItem.value.printSample));

const editSizeWording = computed(() => {
  if (props.addAnotherProduct) {
    return "Add Another Product";
  }
  if (props.addMode) {
    return "Choose color and sizes";
  }
  return "Edit Size and Quantity";
});

const experiments = computed(() => store.getters.experiments);

const shouldShowBrand = computed(() => {
  if (!lineItem.value.brand) return false;
  if (lineItem.value.businessLine === BUSINESS_LINE.BLANKS) {
    return true;
  }
  return experiments.value.abBrandLogoVisibility !== 1;
});

const commonMixpanelProps = computed(() => ({
  color: selectedColor.value?.colorName || lineItem.value.productColor,
  name: lineItem.value.productName,
  line_item: lineItem.value.id,
  user_type: readCookie("name") ? "signed_in" : "guest_user"
}));

const trackingMode = computed(() => (props.addMode ? "add_color" : "edit_size"));

function cleanUp() {
  store.commit("setVariantsData", []);
  store.commit("setProductData", null);
  nextTick(() => {
    errorMessage.value = null;
    isLoading.value = true;
    model.value = {};
    exitReason.value = null;
    if (props.addMode) {
      selectedColor.value = compositions.value?.find(
        c => c.colorName === lineItem.value.productColor
      );
      composite.value = lineItem.value.compositePreview;
    }
  });
}

const close = () => {
  $(modal.value).modal("hide");
};

const onClose = () => {
  exitReason.value = "x_button";
  close();
};

const handleCancel = () => {
  exitReason.value = "cancel";
  close();
  track(`cart_${trackingMode.value}_cancel`, {
    ...commonMixpanelProps.value
  });
};

const handleSizeQuantity = data => {
  model.value[data.id] = { ...data, isExcluded: false };
};

const createVariantModelData = (variant, quantity = null) => ({
  id: variant.id,
  price: variant.price_tiers?.[effectiveTier.value]
    ? parseFloat(variant.price_tiers[effectiveTier.value])
    : parseFloat(variant.price_tiers?.["0"] || 0),
  quantity: quantity !== null ? quantity : variant.quantity || 0,
  size: variant.size_label,
  basePrice: variant.price_tiers ? parseFloat(variant.price_tiers["0"]) || 0 : 0,
  isExcluded: false
});

const getSelectedSampleVariantId = () => {
  const selectedVariant = variantsData.value.find(variant => {
    const modelData = model.value[variant.id];
    return modelData && modelData.quantity > 0;
  });
  return selectedVariant?.id || "";
};

const initializeSampleModel = () => {
  if (!isSample.value) return;

  variantsData.value.forEach(variant => {
    model.value[variant.id] = createVariantModelData(variant);
  });
};

const handleSampleSizeChange = selectedVariantId => {
  if (!selectedVariantId) return;

  // Convert to number since DOM events return strings
  const selectedId = parseInt(selectedVariantId, 10);

  // Set all variants to 0 first, then set selected to 1
  variantsData.value.forEach(variant => {
    const quantity = variant.id === selectedId ? 1 : 0;
    model.value[variant.id] = createVariantModelData(variant, quantity);
  });
};

const isOutOfStock = variantId => {
  const variant = variantsData.value.find(v => v.id === variantId);
  return variant ? variant.inventory === 0 : false;
};

const handleValidate = ({ message }) => {
  errorMessage.value = message;
  isDisabled.value = !!message;
};

const handleCompositePreviewGeneration = color => {
  const placementMap = {
    front_left_chest: "frontLeftChest",
    front: "front",
    back: "back"
  };

  composite.value = {
    ...lineItem.value.compositePreview,
    placements: lineItem.value.compositePreview.placements.map(placement => ({
      ...placement,
      surface: { ...color.surfaces[placementMap[placement.position]] }
    }))
  };
};

const open = async () => {
  cleanUp();
  $(modal.value).modal("show");
  try {
    await Promise.all([
      store.dispatch("fetchVariantsData", lineItem.value),
      store.dispatch("fetchProductData", lineItem.value)
    ]);
    initializeSampleModel();
    isLoading.value = false;
    isSameColor.value = selectedColor.value?.colorName === lineItem.value.productColor;
  } catch (error) {
    errorMessage.value = error.message;
    isLoading.value = false;
  }

  timeStamp.value = Date.now();
  track(`cart_${trackingMode.value}_open`, {
    ...commonMixpanelProps.value
  });
};

const trackCartUpdates = () => {
  Object.values(model.value)
    .filter(item => !item.isExcluded)
    .forEach(({ size, quantity }) => {
      const prevItem = props.sizes.find(item => item.size === size);
      const prevQuantity = prevItem ? prevItem.quantity : 0;

      const trackData = {
        ...commonMixpanelProps.value,
        size,
        quantity
      };

      if (prevQuantity === 0 && quantity > 0) {
        track("cart_size_add", trackData);
      } else if (prevQuantity && quantity === 0) {
        track("cart_size_remove", trackData);
      }
    });
};

const createCartObject = () => {
  const variants = Object.fromEntries(
    Object.entries(model.value)
      .filter(([, data]) => !data.isExcluded || data.quantity > 0)
      .map(([id, data]) => [
        id,
        {
          quantity: data.quantity,
          original_line_item_id: props.isCart
            ? lineItem.value.originalLineItemId
            : lineItem.value.item?.id
        }
      ])
  );

  const placements = {};
  composite.value?.placements?.forEach((placement, index) => {
    if (placement.artworkId) {
      placements[index] = {
        dtf_artwork: {
          id: placement.artworkId,
          enhancementTier: placement.enhancement_tier,
          removeBackground: placement.remove_background,
          jiffyDesignServices: placement.jiffy_design_services
        },
        width: placement.artworkWidth,
        height: placement.artworkHeight,
        position: placement.position,
        original_print_id: props.isCart ? placement.originalPrintId : placement.dtfPrintId
      };
    }
  });

  return {
    selectedColor: selectedColor.value,
    lineItem: lineItem.value,
    variants,
    placements
  };
};

const addToCart = async () => {
  isLoading.value = true;
  await store.dispatch("addToCart", createCartObject());
  if (props.isCart) {
    store.dispatch("shipping/getAllDeliveryDates");
  }
  isLoading.value = false;
  exitReason.value = "add_to_cart";
  track(`cart_${trackingMode.value}_add_to_cart`, {
    ...commonMixpanelProps.value,
    quantity: totalQuantity.value,
    price: totalPrice.value
  });
  close();
};

const save = async () => {
  trackCartUpdates();

  const data = {
    print_assembly_group: {
      id: lineItem.value.assemblyGroupedItems.groupId,
      variants: Object.fromEntries(
        Object.entries(model.value)
          .filter(([, value]) => !value.isExcluded)
          .map(([key, value]) => [key, value.quantity])
      )
    }
  };

  // Include edit_image_manual flag if available
  if (lineItem.value.edit_image_manual !== undefined) {
    data.print_assembly_group.edit_image_manual = lineItem.value.edit_image_manual;
  }

  isLoading.value = true;
  try {
    const response = await store.dispatch("updateVariantQuantities", data);
    const groupsCount = Object.keys(response.order_groups.groups).length;
    isLoading.value = false;

    nextTick(() => {
      exitReason.value = "update_cart";
      close();
      store.dispatch("setCartResponse", response);

      if (!groupsCount) {
        window.location.reload();
      }
    });
  } catch (error) {
    errorMessage.value = error.message;
    isLoading.value = false;
  }

  track(`cart_${trackingMode.value}_update_confirm`, {
    ...commonMixpanelProps.value
  });
};

watch(
  () => errorMessage.value,
  () => {
    if (errorMessage.value) {
      setTimeout(() => {
        errorMessage.value = null;
      }, 12000);
    }
  }
);

watch(
  () => totalQuantity.value,
  (newVal, oldVal) => {
    if (isLoading.value) return;

    if (newVal === 0 && oldVal > 0) {
      errorMessage.value = "Please select at least one size";
      isDisabled.value = true;
    }
  }
);

watch(
  () => totalPrice.value,
  (newVal, oldVal) => {
    if (isLoading.value) return;

    if (newVal !== oldVal) {
      emit("update:totalPrice", newVal);
    }
  }
);

watch(
  () => compositions.value,
  (newVal, oldVal) => {
    if (oldVal.length === 0 && newVal.length > 0) {
      selectedColor.value = newVal.find(c => c.colorName === lineItem.value.productColor);
      isSameColor.value = selectedColor.value?.colorName === lineItem.value.productColor;
    }
  }
);

watch(
  () => variantsData.value,
  newVal => {
    if (newVal.length > 0 && isSample.value) {
      initializeSampleModel();
    }
  }
);

const handleColorSelect = async color => {
  handleCompositePreviewGeneration(color);
  isSameColor.value = color.colorName === lineItem.value.productColor;
  selectedColor.value = color;
  isLoading.value = true;
  model.value = {};

  try {
    await store.dispatch("fetchVariantsData", {
      compositionId: color.id,
      printSizes: lineItem.value.printSizes
    });
    initializeSampleModel();
    isLoading.value = false;
  } catch (error) {
    errorMessage.value = error.message;
    isLoading.value = false;
  }
};

const handleArtworksLoading = isArtworksBeingLoaded => {
  isArtworksLoading.value = isArtworksBeingLoaded;
};

onMounted(() => {
  $(modal.value).on("hide.bs.modal", e => {
    if (isLoading.value) e.preventDefault();
  });

  $(modal.value).on("hidden.bs.modal", () => {
    const durationInSeconds = (Date.now() - timeStamp.value) / 1000;
    track(`cart_${trackingMode.value}_duration`, {
      ...commonMixpanelProps.value,
      duration: `${Math.round(durationInSeconds)} seconds`,
      exit_reason: exitReason.value || "outside_click"
    });
    emit("close");
  });
});

defineExpose({ open, close });
</script>

<template>
  <div
    ref="modal"
    class="made-product-upsert modal"
    :class="{ 'made-product-upsert--cart': isCart }"
    role="dialog"
  >
    <div class="made-product-upsert__modal-box">
      <div
        :aria-label="editSizeWording"
        class="made-product-upsert__modal-header"
        role="heading"
        tabindex="0"
      >
        <h3 class="made-product-upsert__modal-title">{{ editSizeWording }}</h3>
        <button class="made-product-upsert__modal-button-close" type="button" @click="onClose">
          <UseSvg icon="modal-close-big" />
        </button>
      </div>

      <div class="made-product-upsert__modal-body">
        <template v-if="addMode">
          <ProductDetailsV2
            :line-item="lineItem"
            :composite-preview="composite"
            :product-details="productData"
            :selected-color="selectedColor"
            @is-artworks-loading="handleArtworksLoading"
          />
          <ColorPicker
            :compositions="compositions"
            :selected-color="selectedColor"
            :is-loading="isLoading || isArtworksLoading"
            @on-color-select="handleColorSelect"
          />
        </template>
        <ProductDetails
          v-else
          :line-item="lineItem"
          :canvas-image="canvasImage"
          :should-show-brand="shouldShowBrand"
        />

        <div v-if="isLoading && isSample" class="made-product-upsert__sample-skeleton">
          <div
            class="made-product-upsert__sample-skeleton-label made-product-upsert__skeleton-item"
          />
          <div
            class="made-product-upsert__sample-skeleton-select made-product-upsert__skeleton-item"
          />
        </div>
        <div v-else-if="isLoading && !isSample" class="made-product-upsert__quantities-skeleton">
          <div
            v-for="index in 8"
            :key="index"
            class="made-product-upsert__quantities-skeleton-item made-product-upsert__skeleton-item"
          />
        </div>
        <div v-else class="made-product-upsert__size-quantities">
          <div v-if="addMode" class="made-product-upsert__size-quantities-title">
            Enter Quantities and Sizes
          </div>

          <!-- Sample Product Size Selector -->
          <div v-if="isSample" class="made-product-upsert__sample-size-selector">
            <div class="made-product-upsert__sample-size-selector-wrapper">
              <label
                :for="`sample-size-select-${lineItem.id}`"
                class="made-product-upsert__sample-size-label"
              >
                Select Size
              </label>
              <div class="made-product-upsert__sample-size-dropdown">
                <select
                  :id="`sample-size-select-${lineItem.id}`"
                  class="made-product-upsert__sample-size-select"
                  :value="getSelectedSampleVariantId()"
                  @change="handleSampleSizeChange($event.target.value)"
                >
                  <option disabled value="">Choose a size</option>
                  <option
                    v-for="variant in variantsData"
                    :key="variant.id"
                    :value="variant.id"
                    :disabled="isOutOfStock(variant.id)"
                  >
                    {{ variant.size_label }}{{ isOutOfStock(variant.id) ? " (Out of Stock)" : "" }}
                  </option>
                </select>
                <UseSvg icon="chevron-down" class="made-product-upsert__sample-size-chevron" />
              </div>
            </div>
          </div>

          <!-- Regular Product Size Grid -->
          <div v-else class="made-product-upsert__size-quantities-container">
            <SizeQuantityControl
              v-for="variant in variantsData"
              :key="variant.id"
              :line-item="lineItem"
              :variant="variant"
              :selected-color="selectedColor"
              :effective-tier="effectiveTier"
              @input="handleSizeQuantity"
              @validate="handleValidate"
            />
          </div>
        </div>

        <MadeDeliveryEstimates
          v-if="!isCart && selectedColor"
          :composition-id="selectedColor.id"
          :product-id="lineItem.productId"
          :color="selectedColor.colorName"
        />

        <div class="made-product-upsert__modal-footer">
          <div v-if="errorMessage" class="made-product-upsert__error-message">
            <UseSvg class="made-product-upsert__error-message-icon" icon="strong-info" />
            <span class="made-product-upsert__error-message-text">{{ errorMessage }}</span>
          </div>
          <div
            v-else
            class="made-product-upsert__total"
            :class="{ 'made-product-upsert__skeleton-item': isLoading }"
          >
            <span class="made-product-upsert__total-label">Total Quantity:</span>
            <span class="made-product-upsert__total-numbers">
              {{ totalQuantity }} ({{ n(totalPrice, "currency") }})
            </span>
          </div>

          <div class="made-product-upsert__modal-actions">
            <button
              class="made-product-upsert__modal-action made-product-upsert__modal-action--cancel"
              :class="{ 'made-product-upsert__skeleton-item': isLoading }"
              type="button"
              @click="handleCancel"
            >
              Cancel
            </button>
            <button
              v-if="isReorder"
              class="made-product-upsert__modal-action made-product-upsert__modal-action--save"
              :class="{
                'made-product-upsert__modal-action--disabled': disableAddToCart,
                'made-product-upsert__skeleton-item': isLoading
              }"
              :disabled="disableAddToCart"
              type="button"
              @click="addToCart"
            >
              Add to Cart
            </button>
            <button
              v-else
              class="made-product-upsert__modal-action made-product-upsert__modal-action--save"
              :class="{
                'made-product-upsert__modal-action--disabled': disableAddToCart,
                'made-product-upsert__skeleton-item': isLoading
              }"
              :disabled="disableAddToCart"
              type="button"
              @click="save"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/modal.scss";

.made-product-upsert {
  @include crafterpay-modal;

  $this: &;

  &--cart {
    #{$this}__size-quantities-container {
      margin-bottom: 20px;
      min-height: 197px;
    }

    #{$this}__sample-skeleton {
      height: 75px;
    }

    #{$this}__quantities-skeleton {
      height: 217px;
    }

    #{$this}__quantities-skeleton-item {
      height: 100px;
    }
  }

  &__modal-box {
    max-width: 662px;
    overflow-y: auto;

    @include with-tablet {
      margin: auto;
      transform: translateY(-50%);
    }
  }

  &__modal-header {
    min-height: 56px;
    padding: 0 12px;
    border: 0;

    @include with-tablet {
      padding: 0 16px;
    }
  }

  &__modal-title {
    @include with-tablet {
      font-size: 18px;
      line-height: 20px;
    }
  }

  &__modal-button-close {
    top: 4px;
    right: 0px;

    @include with-tablet {
      top: 3px;
      right: 3px;
    }
  }

  &__modal-body {
    padding: 0 10px;

    @include with-tablet {
      padding: 0 16px;
    }
  }

  &__quantities-skeleton {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;

    @include with-tablet {
      grid-template-columns: repeat(auto-fill, 82px);
    }
  }

  &__sample-skeleton {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 16px 0;
    grid-column: 1 / -1;
  }

  &__sample-skeleton-label {
    width: 80px;
    height: 20px;
  }

  &__sample-skeleton-select {
    width: 200px;
    height: 40px;
  }

  &__quantities-skeleton-item {
    height: 89px;

    @include with-tablet {
      &:last-of-type {
        display: none;
      }
    }
  }

  &__size-quantities {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__size-quantities-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-bottom: 10px;

    @include with-tablet {
      grid-template-columns: repeat(auto-fill, 82px);
      margin-bottom: 0;
    }
  }

  &__size-quantities-title {
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: $text-black;
  }

  &__sample-size-selector-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 16px 0;
  }

  &__sample-size-label {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: $text-black;
  }

  &__sample-size-dropdown {
    position: relative;
  }

  &__sample-size-select {
    appearance: none;
    display: block;
    width: 200px;
    border: 2px solid $gray-border;
    border-radius: 8px;
    background-color: $white;
    padding: 8px 40px 8px 12px;
    font-size: 16px;
    font-weight: 500;
    color: $text-black;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: $primary-light;
    }

    &:disabled {
      background-color: lighten($blue-lightest, 2%);
      color: $gray-label;
      cursor: not-allowed;
    }
  }

  &__sample-size-chevron {
    position: absolute;
    right: 12px;
    top: 50%;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    color: $text-black;
    pointer-events: none;
  }

  &__modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    padding-bottom: 16px;
    gap: 12px;

    @include with-tablet {
      flex-direction: row;
      gap: 16px;
      padding: 16px 0;
      border-top: 1px solid $gray-border;
    }
  }

  &__error-message {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 7px 12px;
    border: 1px solid $gray-border;
    border-radius: 8px;
    background-color: $crafterpay-gray-modern-50;
    color: $crafterpay-gray-modern-700;
    font-size: 12px;
    line-height: 16px;
  }

  &__error-message-icon {
    width: 28px;
    height: 28px;
    color: $punch;
  }

  &__total {
    padding-left: 4px;
    font-size: 16px;
  }

  &__total-label {
    margin-right: 4px;
    color: $warm-grey;
  }

  &__total-numbers {
    color: $text-black;
    font-weight: 700;
  }

  &__modal-actions {
    display: flex;
    gap: 9px;
  }

  &__modal-action {
    width: 167px;
    height: 44px;
    border-radius: 7px;
    font-size: 16px;
    font-weight: 700;

    &--save {
      border: 0;
      background-color: $primary-light;
      color: $white;

      &:hover {
        background-color: darken($primary-light, 10%);
      }
    }

    &--cancel {
      border: 1px solid $gray-border;
      background-color: $white;
      color: $primary-light;

      &:hover {
        background-color: darken($white, 5%);
      }
    }

    &--disabled {
      background-color: $gray-border;
      color: $white;
      cursor: not-allowed;
      pointer-events: none;

      &:hover {
        background-color: $gray-border;
      }
    }
  }

  &__skeleton-item {
    @include skeleton(100%);

    border: none;
    pointer-events: none;

    &::before {
      border-radius: 4px;
      background-image: linear-gradient(
        90deg,
        $hercules-loading 0,
        $gray-border-lightest 80px,
        $hercules-loading 200px
      );
    }
  }
}
</style>
